import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import {
    FormattedNumberRaisedSymbol,
    FullScreenTakeover,
    PageBehaviourButton,
    Sizes,
    PrimaryButton,
} from '@ratehub/base-ui';
import { FormattedPrimeRateMessage } from '@ratehub/mtg-common';

import { Messages as MORTGAGE_TYPE_MESSAGES } from '../definitions/MortgageTerms';
import { RatePropType } from '../definitions/BestMortgageRatesPropType';
import ProductTableProviderCell from './ProductTableProviderCell';
import ProductTableBestMortgageRatesMoreDetails from './ProductTableBestMortgageRatesMoreDetails';


function ProductTableBestMortgageRatesLightbox({ rate, onRateClick, onClose }) {
    const intl = useIntl();

    // When the rate is clicked, we open the lead form
    //  we also want to close this modal so there isn't
    //  two modals open and the user doesn't have to
    //  close both modals to get back to the table
    function handleRateClick() {
        onRateClick(rate);
        onClose();
    }

    return (
        <FullScreenTakeover
            displayMode="lightbox"
            onClose={onClose}
            showBranding={false}
            screenReaderLabel={(
                <FormattedMessage
                    id="web-components.ProductTableBestMortgageRatesLightbox.screenReaderLabel"
                    defaultMessage="Modal window to display additional rate information"
                />
            )}
        >
            <Container className="rh-px-1 rh-pb-1">
                <div className="rate-details-container">
                    <div className="rate-cta-container">
                        <PageBehaviourButton
                            data-name="BMRTable-lightbox-rate"
                            size="extra-large"
                            onClick={handleRateClick}
                        >
                            <FormattedNumberRaisedSymbol
                                value={rate.value / 100}
                                format="percent2"
                                symbolClassName="rh-title-xs"
                            />
                        </PageBehaviourButton>
                        <If condition={rate.primeRate != null}>
                            <span className="rh-text-xs rh-my-0 weight-medium">
                                <FormattedPrimeRateMessage
                                    value={rate.value}
                                    primeRate={rate.primeRate}
                                />
                            </span>
                        </If>
                    </div>
                    <ProductTableProviderCell
                        className="rh-mt-0_25"
                        rate={rate}
                        isLogoWrapped={false}
                        hasLogoBorder={true}
                    />
                </div>

                <p className="rh-text-s rh-mt-0_5 rh-mb-1 weight-medium">
                    <Choose>
                        {/* HELOC has term 0 */}
                        <When condition={rate.term === 0}>
                            <FormattedMessage {...MORTGAGE_TYPE_MESSAGES.HELOC_SHORT} />
                        </When>
                        <Otherwise>
                            <FormattedMessage
                                id="web-components.ProductTableBestMortgageRatesLightbox.typeTerm"
                                defaultMessage="{term}-yr {type}"
                                values={{
                                    term: rate.term / 12,
                                    type: intl.formatMessage(MORTGAGE_TYPE_MESSAGES[rate.type]),
                                }}
                            />
                        </Otherwise>
                    </Choose>
                </p>

                <ProductTableBestMortgageRatesMoreDetails
                    regularPayment={rate.regularPayment}
                    lumpSum={rate.lumpSum}
                    rateHold={rate.rateHold}
                    isPreApproval={rate.isPreApproval}
                />

                <PrimaryButton
                    className="rh-mt-1"
                    size="small"
                    message={(
                        <FormattedMessage
                            id="web-components.ProductTableBestMortgageRatesTableCell.ctaInquire"
                            defaultMessage="Inquire"
                        />
                    )}
                    onClick={handleRateClick}
                    data-name="BMRTable-main-cta-lightbox"
                />
            </Container>
        </FullScreenTakeover>
    );
}

ProductTableBestMortgageRatesLightbox.propTypes = {
    rate: RatePropType.isRequired,
    onRateClick: PropTypes.func.isRequired,
    onClose: PropTypes.func,
};

const Container = styled.section`
    > .rate-details-container {
        display: flex;
        flex-wrap: nowrap;
        gap: ${Sizes.SPACING.ONE};
        align-items: flex-start;

        > .rate-cta-container {
            flex-shrink: 0;
        }
    }
`;

export default ProductTableBestMortgageRatesLightbox;
